import React, { useCallback, useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import NotesIcon from '@mui/icons-material/Notes';
import { EquipmentRowProps } from './closeoutTypes';
import { EquipmentCloseout } from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';
import { formatCurrency, formatNumber } from './closeoutHelpers';
import {
  useDeleteEquipmentChangeOrderCloseoutMutation,
  useDeleteEquipmentCloseoutMutation,
  useGetEquipmentChangeOrderCloseouts,
  useGetEquipmentCloseouts,
  useUpdateEquipmentChangeOrderCloseoutMutation,
  useUpdateEquipmentCloseoutMutation,
} from '../../hooks/useCloseoutHooks';
import {
  TableCell,
  TableRow,
  TextField,
  useTheme,
  alpha,
  Tooltip,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import NotesDialog from './NotesDialog';
import { useLockBid } from '../../utils/useLockBid';

// Update sxSwitch to use theme colors
export const sxSwitch = (num: number, theme: any) => {
  return {
    backgroundColor:
      num > 0
        ? theme.palette.mode === 'dark'
          ? alpha(theme.palette.warning.main, 0.15)
          : alpha(theme.palette.warning.light, 0.2)
        : theme.palette.mode === 'dark'
          ? alpha(theme.palette.action.disabled, 0.1)
          : alpha(theme.palette.action.disabled, 0.1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius + 'px',
    '& input': {
      color: theme.palette.text.primary,
    },
  };
};

export const EquipmentRow: React.FC<EquipmentRowProps> = ({ closeout }) => {
  const theme = useTheme();
  const { lockBid } = useLockBid();

  // Common styles matching TotalPricingRow and TotalPricingHeader
  const rowStyle = {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.1)
          : alpha(theme.palette.common.black, 0.04),
    },
    '&:nth-of-type(even)': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.02)
          : alpha(theme.palette.common.black, 0.02),
    },
  };

  const cellStyle = {
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap' as const,
  };

  const inputCellStyle = {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  };
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const [quantity, setQuantity] = useState<string>(formatNumber(closeout.quantity));
  const [duration, setDuration] = useState<string>(formatNumber(closeout.duration));
  const [unitcost, setUnitCost] = useState<string>(formatCurrency(closeout.unitcost));
  const [notesDisplay, setNotesDisplay] = useState<string>(closeout.notes);
  const [notesDialogOpen, setNotesDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const { mutateAsync: updateEstimateCloseout } = useUpdateEquipmentCloseoutMutation();
  const { mutateAsync: deleteEstimateCloseout } = useDeleteEquipmentCloseoutMutation();
  const { refetch: refetchEstimateCloseout } = useGetEquipmentCloseouts();

  const { mutateAsync: updateChangeOrderCloseout } =
    useUpdateEquipmentChangeOrderCloseoutMutation();
  const { mutateAsync: deleteChangeOrderCloseout } =
    useDeleteEquipmentChangeOrderCloseoutMutation();
  const { refetch: refetchChangeOrderCloseout } = useGetEquipmentChangeOrderCloseouts();

  const updateCloseout = selectedEstimate?.estimateid
    ? updateEstimateCloseout
    : updateChangeOrderCloseout;
  const deleteCloseout = selectedEstimate?.estimateid
    ? deleteEstimateCloseout
    : deleteChangeOrderCloseout;
  const refetch = selectedEstimate?.estimateid
    ? refetchEstimateCloseout
    : refetchChangeOrderCloseout;

  useEffect(() => {
    setQuantity(formatNumber(closeout.quantity));
    setDuration(formatNumber(closeout.duration));
    setUnitCost(formatCurrency(closeout.unitcost));
    setNotesDisplay(closeout.notes);
  }, [closeout]);

  type Field = keyof Pick<
    EquipmentCloseout.AsObject,
    'quantity' | 'duration' | 'unitcost' | 'notes'
  >;

  const handleFieldChange = useCallback(
    async (field: Field, value: string) => {
      // Allow only numbers with up to 2 decimals.
      const regex = /^[0-9]*\.?[0-9]{0,2}$/;
      if (field !== 'notes' && (!regex.test(value) || Number(value) > 99999999.99)) return;
      await updateCloseout(
        {
          ...closeout,
          [field]: value,
        },
        {
          onSuccess: () => {
            refetch();
          },
        }
      );
    },
    [closeout, updateCloseout, refetch]
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    deleteCloseout(closeout.closeoutid, {
      onSuccess: () => {
        refetch();
      },
    });
    setDeleteDialogOpen(false);
  }, [closeout, deleteCloseout, refetch]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleOpenNotesDialog = () => {
    setNotesDialogOpen(true);
  };

  const handleCloseNotesDialog = () => {
    setNotesDialogOpen(false);
  };

  const handleSaveNotes = async (notes: string) => {
    setNotesDisplay(notes);
    await updateCloseout(
      {
        ...closeout,
        notes: notes,
      },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };

  const hasNotes = !!closeout.notes && closeout.notes.trim().length > 0;

  return (
    <>
      <TableRow sx={rowStyle}>
        <TableCell sx={cellStyle}>{closeout.name}</TableCell>
        <TableCell sx={inputCellStyle}>
          <TextField
            disabled={lockBid()}
            type="text"
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
            onBlur={e => handleFieldChange('quantity', e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              min: 0,
              max: 99999999.99,
              step: '0.01',
              'aria-label': 'Quantity override',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                ...sxSwitch(closeout.quantity, theme),
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.05),
                },
              },
            }}
          />
        </TableCell>
        <TableCell sx={inputCellStyle}>
          <TextField
            disabled={lockBid()}
            type="text"
            value={duration}
            onChange={e => setDuration(e.target.value)}
            onBlur={e => handleFieldChange('duration', e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              min: 0,
              max: 99999999.99,
              step: '0.01',
              'aria-label': 'Duration override',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                ...sxSwitch(closeout.duration, theme),
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.05),
                },
              },
            }}
          />
        </TableCell>
        <TableCell sx={inputCellStyle}>
          <TextField
            disabled={lockBid()}
            type="text"
            value={unitcost}
            onChange={e => setUnitCost(e.target.value)}
            onBlur={e => handleFieldChange('unitcost', e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              min: 0,
              max: 99999999.99,
              step: '0.01',
              'aria-label': 'Unit cost override',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                ...sxSwitch(closeout.unitcost, theme),
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.05),
                },
              },
            }}
          />
        </TableCell>
        <TableCell sx={cellStyle}>{formatCurrency(closeout.totalcost)}</TableCell>
        <TableCell sx={cellStyle}>
          <Tooltip title={hasNotes ? 'View/Edit Notes' : 'Add Notes'} arrow placement="left">
            <Badge
              color="primary"
              variant="dot"
              invisible={!hasNotes}
              sx={{ '& .MuiBadge-dot': { right: 2, top: 2 } }}
            >
              <IconButton
                onClick={handleOpenNotesDialog}
                size="small"
                sx={{
                  mr: 1,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
                aria-label={hasNotes ? 'View or edit notes' : 'Add notes'}
              >
                <NotesIcon fontSize="small" />
              </IconButton>
            </Badge>
          </Tooltip>
          <Tooltip title="Delete this equipment entry" arrow placement="left">
            <IconButton
              onClick={handleDeleteClick}
              size="small"
              sx={{
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
              }}
              aria-label={`Delete ${closeout.name} equipment entry`}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <NotesDialog
        open={notesDialogOpen}
        notes={notesDisplay}
        onClose={handleCloseNotesDialog}
        onSave={handleSaveNotes}
        title={`${closeout.name} Note`}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
          },
        }}
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the {closeout.name} equipment entry? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
